import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, memo } from "react";
import JobIcon from "./icons/JobIcon";
import JobReportIcon from "./icons/JobReportIcon";
import MenuIcon from "./icons/MenuIcon";
import SupportIcon from "./icons/SupportIcon";
import TimeReportIcon from "./icons/TimeReportIcon";
import WhiteCloseLight from "./icons/WhiteCloseLight";
import ScheduleIcon from "./icons/ScheduleIcon";
import StatisticIcon from "./icons/StatisticIcon";
import WorkPlaceIcon from "./icons/WorkPlaceIcon";
import ProfileIcon from "./icons/ProfileIcon";
import { useAuth } from "../auth/AuthContext";
import { USER_KEY } from "../auth/session";
import { tenant } from "@/theme/tenant";

// TODO: Phase 4-5 - Import actual routes from routes.ts and API hooks
const routes = {
	schedule: "/schema",
	jobs: "/uppdrag",
	reportsTime: "/rapporter/tidrapporter",
	consultantReports: "/rapporter/konsultrapporter",
	stats: "/statistik",
	workPlace: "/arbetsplats",
	myProfile: "/minprofil",
	support: "/support",
	signIn: "/logga-in",
};

interface Props {
	isOpen?: boolean;
	onToggle: () => void;
}

interface User {
	firstName?: string;
	lastName?: string;
}

// Hoisted out of `Navbar` so re-renders of the parent don't create a new
// component identity, which would otherwise unmount/remount these subtrees
// (causing the avatar to visibly flicker on any in-flight request).
const MenuItem = ({
	title,
	url,
	icon,
	activePath,
	className = "flex",
}: {
	title: string;
	url: string;
	icon: React.ReactNode;
	activePath?: string;
	/** Display classes, e.g. to hide the item on some screen sizes */
	className?: string;
}) => {
	const isActive = activePath === url;
	return (
		<Link
			to={url}
			className={`
				${className} items-center gap-4 px-6 py-3
				!text-white no-underline
				transition-opacity hover:opacity-80
				${isActive ? "opacity-75" : "opacity-100"}
			`}
		>
			<div className="w-6 h-6 flex items-center justify-center flex-shrink-0">{icon}</div>
			<span className="text-md font-semibold">{title}</span>
		</Link>
	);
};

const MenuItems = ({ activePath }: { activePath?: string }) => (
	<nav className="flex flex-col gap-1.5">
		{/* The week schedule needs a wide screen, so phones don't get the item */}
		<MenuItem
			activePath={activePath}
			title="Schema"
			url={routes.schedule}
			icon={<ScheduleIcon />}
			className="hidden lg:flex"
		/>
		<MenuItem activePath={activePath} title="Uppdrag" url={routes.jobs} icon={<JobIcon />} />
		<MenuItem activePath={activePath} title="Tidrapporter" url={routes.reportsTime} icon={<TimeReportIcon />} />
		<MenuItem activePath={activePath} title="Konsultrapporter" url={routes.consultantReports} icon={<JobReportIcon />} />
		<MenuItem activePath={activePath} title="Statistik" url={routes.stats} icon={<StatisticIcon />} />
		<MenuItem activePath={activePath} title="Arbetsplats" url={routes.workPlace} icon={<WorkPlaceIcon />} />
		<MenuItem activePath={activePath} title="Min profil" url={routes.myProfile} icon={<ProfileIcon />} />
		<MenuItem activePath={activePath} title="Hjälp" url={routes.support} icon={<SupportIcon />} />
	</nav>
);

const ProfileAndLogout = memo(({ user, onLogout }: { user: User | null; onLogout: () => void }) => {
	const userInitials = (user?.firstName ? user.firstName[0] : "") + (user?.lastName ? user.lastName[0] : "");
	return (
		<div className="flex items-center gap-4 px-6">
			<div className="w-[50px] h-[50px] bg-white rounded-full flex items-center justify-center">
				<span className="!text-brand text-xl font-bold">{userInitials || ""}</span>
			</div>
			<div className="flex flex-col">
				<span className="!text-white text-base font-normal">{user?.firstName + " " + user?.lastName}</span>
				<button
					className="!text-white text-xs font-normal underline bg-transparent border-none cursor-pointer p-0 text-left"
					onClick={onLogout}
				>
					Logga ut
				</button>
			</div>
		</div>
	);
});

const MenuToggle = ({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) => (
	<div className="block lg:hidden cursor-pointer" onClick={onToggle}>
		{isOpen ? (
			<WhiteCloseLight />
		) : (
			<div className="flex flex-row items-center gap-2">
				<MenuIcon />
				<span className="!text-white text-sm font-semibold">Menu</span>
			</div>
		)}
	</div>
);

export const Navbar: React.FC<Props> = ({ isOpen = false, onToggle }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { logout } = useAuth();
	const [user, setUser] = useState<User | null>(null);

	// Load user data from localStorage on mount
	useEffect(() => {
		const userJson = localStorage.getItem(USER_KEY);
		if (userJson) {
			try {
				const userData = JSON.parse(userJson);
				setUser(userData);
			} catch (error) {
				console.error("Failed to parse user data from localStorage:", error);
			}
		}
	}, []);

	return (
		<nav
			className={`
				flex flex-col justify-between
				${isOpen ? "h-screen" : "h-[53px]"} lg:h-screen
				w-full lg:w-[280px]
				bg-brand
				rounded-none lg:rounded-r-[30px]
				px-4 pb-4 lg:px-0 lg:pb-12
			`}
		>
			{/* Header */}
			<div className="flex flex-col">
				<div className="flex flex-row justify-between lg:justify-center items-center w-full h-[53px] lg:h-auto lg:pt-12 lg:pb-16">
					<div className="block lg:hidden">
						<img
							src={tenant.logoSmall ?? tenant.logo}
							alt={tenant.name}
							className="logo w-auto h-auto max-w-[120px] max-h-[32px] object-contain"
						/>
					</div>

					<MenuToggle isOpen={isOpen} onToggle={onToggle} />

					<div className="hidden lg:block cursor-pointer px-6" onClick={() => navigate(routes.schedule)}>
						<img
							src={tenant.logo}
							alt={tenant.name}
							className="logo mx-auto w-auto h-auto max-w-[160px] max-h-[64px] object-contain"
						/>
					</div>
				</div>

				{/* Menu Items */}
				{isOpen && (
					<div className="flex flex-col lg:hidden pt-8">
						<MenuItems activePath={location?.pathname} />
					</div>
				)}
				<div className="hidden lg:flex flex-col pt-8">
					<MenuItems activePath={location?.pathname} />
				</div>
			</div>

			{/* Profile Section */}
			{isOpen && (
				<div className="flex lg:hidden pt-8">
					<ProfileAndLogout user={user} onLogout={logout} />
				</div>
			)}
			<div className="hidden lg:flex pt-8">
				<ProfileAndLogout user={user} onLogout={logout} />
			</div>
		</nav>
	);
};
